import React, { useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { motion } from 'framer-motion';

const CONFETTI_Z = 10000; // Above overlay (z-9999) so confetti is visible

/**
 * Full-screen celebration overlay with confetti and success message.
 * Auto-dismisses after 2 seconds. Wrap in AnimatePresence for exit animation.
 */
export default function BookingSuccessCelebration({ onClose, title = 'Your trip has been booked!' }) {
  const hasFired = useRef(false);

  const fireConfetti = useCallback(() => {
    if (hasFired.current) return;
    hasFired.current = true;

    const colors = ['#2A3868', '#f97316', '#4E6199', '#4E6199', '#22c55e', '#2A3868', '#ffffff'];
    const opts = { colors, zIndex: CONFETTI_Z };

    // Initial full-screen burst from center
    confetti({
      ...opts,
      particleCount: 150,
      spread: 120,
      origin: { y: 0.5, x: 0.5 },
      startVelocity: 45,
    });

    // Side cannons for ~2 seconds
    const duration = 2000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({ ...opts, particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.8 }, scalar: 1.2 });
      confetti({ ...opts, particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.8 }, scalar: 1.2 });
      confetti({ ...opts, particleCount: 3, angle: 90, spread: 100, origin: { x: 0.5, y: 0 }, scalar: 1.1 });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();

    setTimeout(() => {
      confetti({
        ...opts,
        particleCount: 80,
        spread: 100,
        origin: { y: 0.6, x: 0.5 },
        startVelocity: 35,
      });
    }, 300);
  }, []);

  useEffect(() => {
    fireConfetti();
  }, [fireConfetti]);

  // Auto-dismiss after 2 seconds
  useEffect(() => {
    const t = setTimeout(onClose, 2000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm pointer-events-none"
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className="relative text-center px-8 py-12 max-w-md"
      >
        <div className="rounded-3xl bg-gradient-to-br from-white/10 via-white/5 to-transparent border border-white/20 p-10 shadow-2xl">
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="text-3xl md:text-4xl font-light text-white tracking-tight"
          >
            {title}
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
}
